// @givmo/app-admin — employees list presentation helpers.
//
// The tolerant list extraction for the employees list endpoint
// (`GET /admin/company/employees`) is the shared `extractList` from
// `@givmo/api-client/list` — the employees page calls it directly with
// `CompanyEmployeeOut`. This module keeps the employees-specific display helper.

import type { CompanyEmployeeOut } from '@givmo/api-client';

/**
 * Full display name from `first_name` + `last_name`, guarding nulls so a missing surname never
 * renders "First null" (the Flutter `getFullName()` interpolates both unguarded — improved here).
 * Falls back to the username/work email when both name parts are blank.
 */
export function employeeFullName(employee: CompanyEmployeeOut): string {
  const name = [employee.first_name, employee.last_name].filter(Boolean).join(' ').trim();
  if (name.length > 0) return name;
  return employee.username || employee.work_email || '';
}

/**
 * The removal confirmation's note (CAM-R38). Removing an employee cancels their PENDING match
 * requests of this company — the backend does it in the removal's own transaction — so the dialog
 * says so before the act. Approved word for word as "Removing {name} also cancels their pending
 * match requests."; a selection of several names the count in place of the name.
 */
export function removalCancelsRequestsNote(employees: readonly CompanyEmployeeOut[]): string {
  const [only] = employees;
  const who =
    employees.length === 1 && only !== undefined
      ? employeeFullName(only)
      : `${employees.length} employees`;
  return `Removing ${who} also cancels their pending match requests.`;
}
